// src/ChatbotWidget.js - Enhanced with Slot-Filling Support (updated)
import React, { useState, useEffect, useRef } from "react";
import { FaMicrophone, FaPaperPlane } from "react-icons/fa6";
import { LuUserSearch } from "react-icons/lu";
import { SiGoogleassistant } from "react-icons/si";
import { PiWaveformBold } from "react-icons/pi";
import logo from "./utils/logo2.png";
import Orb from "./Orb";
import LoginModal from "./LoginModal";
import { getValidAccessToken } from "./authClient";
import { loadUser, clearTokens, loadTokens } from "./auth";
import { FaPaperclip } from "react-icons/fa";

const BACKEND_URL = "https://staging.parkengage.ai/api";

let sharedAudioCtx = null;

const ChatbotWidget = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [started, setStarted] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [listening, setListening] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());

  // ✨ NEW: Slot-filling state
  const [slotFillingSessionId, setSlotFillingSessionId] = useState(null);
  const [isFollowup, setIsFollowup] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const recognitionRef = useRef(null);
  const transcriptSeenRef = useRef(false);
  const transcriptTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const ignoreSttRef = useRef(false);
  const fileInputRef = useRef(null);

  // normalize user on initial load
  const normalizeUser = (raw) => {
    if (!raw) return null;

    let inner = raw;

    if (typeof inner === "string") {
      try {
        inner = JSON.parse(inner);
      } catch { }
    }

    if (inner.data && typeof inner.data === "object") {
      if (inner.data.user && typeof inner.data.user === "object") inner = inner.data.user;
      else inner = inner.data;
    } else if (inner.user && typeof inner.user === "object") {
      inner = inner.user;
    }

    if (inner.data && typeof inner.data === "object") {
      inner = inner.data;
    }

    const name =
      inner.name ||
      inner.full_name ||
      inner.first_name ||
      inner.username ||
      (inner.profile && inner.profile.name) ||
      inner.email ||
      null;

    return {
      id: inner.id || inner.user_id || null,
      name,
      email: inner.email || null,
      raw: inner,
      _original: raw,
    };
  };

  const [user, setUser] = useState(() => normalizeUser(loadUser()));

  const getDisplayName = (u) => {
    if (!u) return "User";
    return u.name || "User";
  };

  const userGestureRef = useRef(false);

  useEffect(() => {
    const style = document.createElement("style");
    style.id = "chatbot-widget-ios-fixes";
    style.innerHTML = `
      .no-scroll-touch { touch-action: none !important; -webkit-user-select: none; -webkit-touch-callout: none; }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById("chatbot-widget-ios-fixes");
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === null) return;
      if (e.key === "app_user" || e.key === "app_tokens" || e.key === null) {
        try {
          const fresh = normalizeUser(loadUser());
          setUser(fresh);
        } catch {
          setUser(null);
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    const unlock = () => {
      if (!sharedAudioCtx) {
        sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
        console.log("🔓 Global AudioContext created");
      }
      if (sharedAudioCtx.state === "suspended") {
        sharedAudioCtx.resume();
        console.log("🎧 AudioContext resumed");
      }
    };

    window.addEventListener("touchstart", unlock, { once: true });
    window.addEventListener("click", unlock, { once: true });

    return () => {
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("click", unlock);
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      console.warn("⚠️ SpeechRecognition not supported in this browser.");
      recognitionRef.current = null;
      return;
    }

    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = "en-US";

    rec.onresult = (event) => {
      if (ignoreSttRef.current) return;
      let interim = "";
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const res = event.results[i];
        if (res.isFinal) final += res[0].transcript;
        else interim += res[0].transcript;
      }

      if (interim || final) {
        transcriptSeenRef.current = true;
        if (transcriptTimeoutRef.current) {
          clearTimeout(transcriptTimeoutRef.current);
          transcriptTimeoutRef.current = null;
        }
      }

      if (interim) setInput(interim);
      if (final) setInput(final.trim());
    };

    rec.onerror = (err) => {
      console.warn("SpeechRecognition error:", err);
      setListening(false);
      if (transcriptTimeoutRef.current) {
        clearTimeout(transcriptTimeoutRef.current);
        transcriptTimeoutRef.current = null;
      }
    };

    rec.onend = () => {
      setListening(false);
      ignoreSttRef.current = false;
      if (transcriptTimeoutRef.current) {
        clearTimeout(transcriptTimeoutRef.current);
        transcriptTimeoutRef.current = null;
      }
    };

    rec.onstart = () => {
      setListening(true);
    };

    recognitionRef.current = rec;
    return () => {
      try {
        if (recognitionRef.current) {
          recognitionRef.current.onresult = null;
          recognitionRef.current.onend = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.onstart = null;
          recognitionRef.current = null;
        }
      } catch { }
    };
  }, []);

  const speak = async (text, langOverride = null) => {
    // TTS disabled in this version
    return;
  };

  // ✨ ENHANCED: Send message with slot-filling support
  const handleSend = async (e) => {
    e?.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;

    // Add user message to chat
    const userMessage = { sender: "user", text: trimmed };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setStarted(true);
    setIsProcessing(true);

    try {
      // Get auth tokens
      const tokens = loadTokens();
      let partnerId = null;
      let partnerSecret = null;

      if (user && user.raw) {
        partnerId = user.raw.partner_id || user.id || null;
        if (tokens && tokens.partner_secret) {
          partnerSecret = tokens.partner_secret;
        }
      }

      // Build payload skipping null/undefined values
      const payload = { query: trimmed };
      if (partnerId !== null && partnerId !== undefined) payload.partner_id = partnerId;
      if (partnerSecret) payload.partner_secret = partnerSecret;
      if (slotFillingSessionId) payload.session_id = slotFillingSessionId;
      // Always send explicit boolean so backend gets a definite value
      payload.is_followup = Boolean(isFollowup);

      console.log("📤 Sending to backend:", payload);

      // ✨ ENHANCED: Call backend with slot-filling parameters
      const response = await fetch(`${BACKEND_URL}/route`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      // Always read response text and try parse JSON (useful for 422 details)
      const respText = await response.text();
      let data = {};
      try {
        data = respText ? JSON.parse(respText) : {};
      } catch (err) {
        console.error("Failed to parse backend response as JSON:", respText);
        throw new Error(`Unexpected non-JSON response from server (status ${response.status})`);
      }

      if (!response.ok) {
        console.error("Backend returned non-OK status:", response.status, data);
        // display backend error detail to user (developer-friendly)
        const serverMsg = data.detail ? JSON.stringify(data.detail) : (data.error || JSON.stringify(data));
        setMessages(prev => [...prev, { sender: "bot", text: `Server error (${response.status}): ${serverMsg}` }]);
        // reset slot-filling on server error
        setSlotFillingSessionId(null);
        setIsFollowup(false);
        return;
      }

      console.log("📥 Response from backend:", data);

      // ✨ ENHANCED: Handle different response statuses
      if (data.status === "needs_input") {
        // Bot needs more information - show the question
        const botMessage = {
          sender: "bot",
          text: data.question,
          isQuestion: true  // ✨ Mark as a question for styling
        };
        setMessages((prev) => [...prev, botMessage]);

        // ✨ Update slot-filling state
        setSlotFillingSessionId(data.session_id || null);
        setIsFollowup(true);

        console.log("🔄 Slot-filling activated, session:", data.session_id);

      } else if (data.status === "success") {
        // Success - show the answer
        const botMessage = {
          sender: "bot",
          text: data.answer || "I received your message but don't have a response."
        };
        setMessages((prev) => [...prev, botMessage]);

        // ✨ Clear slot-filling state on success
        setSlotFillingSessionId(null);
        setIsFollowup(false);

        console.log("✅ Query completed successfully");

      } else {
        // Error or unknown status
        const botMessage = {
          sender: "bot",
          text: data.answer || data.message || "Sorry, something went wrong. Please try again."
        };
        setMessages((prev) => [...prev, botMessage]);

        // ✨ Clear slot-filling state on error
        setSlotFillingSessionId(null);
        setIsFollowup(false);
      }

    } catch (error) {
      console.error("❌ Error communicating with backend:", error);
      const errorMessage = {
        sender: "bot",
        text: "Sorry, I'm having trouble connecting. Please try again.",
      };
      setMessages((prev) => [...prev, errorMessage]);

      // ✨ Clear slot-filling state on error
      setSlotFillingSessionId(null);
      setIsFollowup(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMicButtonPress = () => {
    if (!recognitionRef.current) {
      alert("Speech recognition is not supported on this browser/device.");
      return;
    }

    if (listening) {
      try {
        recognitionRef.current.stop();
      } catch { }
      setListening(false);
      return;
    }

    ignoreSttRef.current = false;
    transcriptSeenRef.current = false;

    if (transcriptTimeoutRef.current) {
      clearTimeout(transcriptTimeoutRef.current);
    }

    transcriptTimeoutRef.current = setTimeout(() => {
      if (!transcriptSeenRef.current) {
        console.log("⏱ No transcript within 3s, stopping recognition.");
        if (recognitionRef.current && listening) {
          try {
            recognitionRef.current.stop();
          } catch { }
        }
      }
    }, 3000);

    try {
      recognitionRef.current.start();
      setListening(true);
    } catch (err) {
      console.warn("Recognition start error:", err);
      setListening(false);
      if (transcriptTimeoutRef.current) {
        clearTimeout(transcriptTimeoutRef.current);
        transcriptTimeoutRef.current = null;
      }
    }
  };

  const handleIconClick = () => {
    setShowAnimation(true);
  };

  const handleLogout = () => {
    if (!window.confirm("Are you sure you want to log out?")) return;

    clearTokens();
    setUser(null);

    // ✨ Clear slot-filling state on logout
    setSlotFillingSessionId(null);
    setIsFollowup(false);

    try {
      if (recognitionRef.current && listening) {
        try { recognitionRef.current.stop(); } catch { }
      }
    } catch (e) { }

    try {
      if (window.speechSynthesis && window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel();
      }
    } catch (e) { }

    ignoreSttRef.current = false;
    if (transcriptTimeoutRef.current) {
      clearTimeout(transcriptTimeoutRef.current);
      transcriptTimeoutRef.current = null;
    }
    setMessages([]);
    setInput("");
    setStarted(false);
    setShowAnimation(false);
    setListening(false);
    window.scrollTo({ top: 0, behavior: "auto" });

    alert("You have been logged out successfully.");
  };

  return (
    <div className="flex flex-col min-h-screen bg-white relative px-4 sm:px-6 md:px-10 py-6 sm:py-8">
      {/* Logo */}
      <div
        className="
        absolute top-4 sm:top-5 md:top-6 
        left-1/2 transform -translate-x-1/2
        lg:left-10 lg:top-6 lg:transform-none
      "
      >
        <img
          src={logo}
          alt="ParkEngage Logo"
          className="h-10 sm:h-12 md:h-14 w-auto object-contain transition-all duration-300"
        />
      </div>

      {/* Auth */}
      <div className="absolute top-4 right-4 z-50 flex items-center space-x-3">
        {user ? (
          <>
            <div className="text-sm text-gray-700">
              Hi, {getDisplayName(user)}
            </div>
            <button
              onClick={handleLogout}
              className="px-3 py-1 rounded border text-sm"
            >
              Logout
            </button>
          </>
        ) : (
          <button
            onClick={() => setLoginOpen(true)}
            className="px-3 py-1 rounded border text-sm"
          >
            Login
          </button>
        )}
      </div>

      {/* ✨ NEW: Slot-filling indicator */}
      {slotFillingSessionId && (
        <div className="absolute top-16 right-4 z-40 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700">
          <span className="animate-pulse">●</span> Collecting information...
        </div>
      )}

      {loginOpen && (
        <LoginModal
          onClose={() => setLoginOpen(false)}
          onLoginSuccess={(u) => {
            setUser(normalizeUser(u));
            setAddModalOpen(true);
          }}
        />
      )}

      <div className="flex flex-col items-center justify-center flex-1 mt-10 lg:mt-6">
        {showAnimation ? (
          <Orb onClose={() => setShowAnimation(false)} />
        ) : (
          <>
            {!started && (
              <h1
                className="text-xl sm:text-2xl md:text-3xl lg:text-2xl font-semibold text-center px-4 mb-2 sm:mb-3"
                style={{ color: "#305688ff" }}
              >
                Experience true digital parking with{" "}
                <span className="font-bold text-blue-400">ParkEngage</span>
              </h1>
            )}

            {/* Chat Wrapper */}
            <div
              className={`flex flex-col w-full max-w-lg sm:max-w-3xl md:max-w-4xl lg:max-w-5xl
              ${started ? "h-[76vh] sm:h-[81vh] lg:h-[84vh]" : "h-auto"} 
              mt-2
              ${started ? "" : "border border-gray-200 rounded-2xl shadow-lg bg-white"}
              overflow-hidden transition-all duration-500`}
            >
              {started && (
                <div className="flex-1 overflow-y-auto scrollbar-hidden p-5 sm:p-7 space-y-6">
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex items-start ${msg.sender === "user"
                          ? "justify-end"
                          : "justify-start"
                        }`}
                    >
                      <div
                        className={`${msg.sender === "user"
                            ? "order-2 ml-2"
                            : "mr-2"
                          } flex items-center`}
                      >
                        {msg.sender === "user" ? (
                          <LuUserSearch className="text-[#174A8C] text-lg" />
                        ) : (
                          <SiGoogleassistant className="text-gray-800 text-lg" />
                        )}
                      </div>

                      <div
                        className={`max-w-[80%] break-words whitespace-pre-wrap
                        text-base
                        ${msg.sender === "user"
                            ? "bg-[#e3f2fd] text-[#174A8C] px-4 py-2 rounded-xl"
                            : msg.isQuestion
                            ? "text-gray-800 bg-yellow-50 border-l-4 border-yellow-400 px-4 py-2 rounded-r-xl"  // ✨ Special styling for questions
                            : "text-gray-800"
                          }`}
                      >
                        {msg.text}
                      </div>
                    </div>
                  ))}

                  {/* ✨ Processing indicator */}
                  {isProcessing && (
                    <div className="flex items-start justify-start">
                      <div className="mr-2">
                        <SiGoogleassistant className="text-gray-800 text-lg" />
                      </div>
                      <div className="text-gray-500 italic">
                        Thinking...
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}

              {/* Input */}
              <form
                onSubmit={handleSend}
                className="flex items-center w-full px-4 sm:px-6 py-3 bg-white"
              >
                <div className="flex items-start w-full bg-gray-100 rounded-lg px-4 py-3.5 shadow-md gap-3">
                  {/* File Upload Icon */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-[#174A8C] text-lg mt-1 hover:scale-110 transition-transform"
                    title="Upload file"
                    disabled={isProcessing}
                  >
                    <FaPaperclip />
                  </button>

                  {/* Text Input */}
                  <textarea
                    ref={inputRef}
                    className="flex-1 bg-transparent outline-none resize-none overflow-hidden text-gray-800 placeholder-gray-500 leading-normal text-base"
                    placeholder={isFollowup ? "Please provide the requested information..." : "Ask anything..."}  // ✨ Dynamic placeholder
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      e.target.style.height = "auto";
                      e.target.style.height = `${e.target.scrollHeight}px`;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend(e);
                      }
                    }}
                    rows={1}
                    style={{ maxHeight: "120px" }}
                    disabled={isProcessing}
                  />

                  {/* Send / Mic / Waveform */}
                  {input.trim() ? (
                    <button
                      type="submit"
                      className="text-[#174A8C] text-lg mx-2 hover:scale-110 transition-transform disabled:opacity-50"
                      disabled={isProcessing}
                    >
                      <FaPaperPlane />
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className={`text-[#174A8C] text-lg mx-2 transition-transform ${listening
                          ? "text-red-600 animate-pulse scale-110"
                          : "hover:scale-110"
                          } disabled:opacity-50`}
                        onClick={handleMicButtonPress}
                        title="Dictate"
                        disabled={isProcessing}
                      >
                        <FaMicrophone />
                      </button>

                      <button
                        type="button"
                        className="text-[#174A8C] text-lg hover:scale-110 transition-transform disabled:opacity-50"
                        onClick={handleIconClick}
                        title="Use voice mode"
                        disabled={isProcessing}
                      >
                        <PiWaveformBold />
                      </button>
                    </>
                  )}

                  {/* Hidden File Input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        console.log("Selected file:", file);
                        // handle upload logic here
                      }
                    }}
                  />
                </div>
              </form>

            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatbotWidget;
